import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { api } from '@/api/http';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/dashboard/grupos')({ component: GruposPage });

const STATUS_LABELS: Record<string, string> = {
  OPEN: 'Aberto', FULL: 'Cheio', CONFIRMED: 'Confirmado', DONE: 'Realizado', CANCELLED: 'Cancelado',
};
const STATUS_STYLES: Record<string, string> = {
  OPEN: 'bg-teal-50 text-teal-700 border-teal-200',
  FULL: 'bg-amber-50 text-amber-700 border-amber-200',
  CONFIRMED: 'bg-teal-50 text-teal-700 border-teal-200',
  DONE: 'bg-slate-50 text-slate-600 border-slate-200',
  CANCELLED: 'bg-red-50 text-red-600 border-red-200',
};

function GruposPage() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const { data = [], isLoading } = useQuery({
    queryKey: ['my-groups'],
    queryFn: () => api.get('groups/my-groups').json<any[]>(),
  });

  const cancelGroup = useMutation({
    mutationFn: (id: string) => api.post(`groups/${id}/cancel`).json<any>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-groups'] }),
  });

  return (
    <div className="max-w-5xl space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">Sessões em grupo</h1>
          <p className="text-slate-500 text-sm mt-1">Conduza encontros temáticos ou terapia em grupo</p>
        </div>
        <Button onClick={() => setShowForm((v) => !v)} className="bg-coral-400 hover:bg-coral-500 text-white">
          {showForm ? 'Cancelar' : '+ Criar grupo'}
        </Button>
      </div>

      {showForm && <GroupForm onDone={() => setShowForm(false)} />}

      {isLoading ? (
        <p className="text-slate-500 text-sm">Carregando...</p>
      ) : data.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <p className="font-bold text-slate-700">Nenhum grupo agendado</p>
            <p className="text-sm text-slate-500 mt-2">Use grupos para sessões temáticas com várias pessoas.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {data.map((g: any) => {
            const filled = g._count?.participants ?? g.participants?.length ?? 0;
            const pct = g.maxParticipants > 0 ? (filled / g.maxParticipants) * 100 : 0;
            const isCancellable = g.status !== 'CANCELLED' && g.status !== 'DONE';
            return (
              <Card key={g.id}>
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-bold text-slate-800">{g.title}</h3>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {new Date(g.scheduledAt).toLocaleString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })} · {g.durationMin}min
                      </p>
                    </div>
                    <span className={cn('text-[10px] px-2 py-0.5 rounded border whitespace-nowrap', STATUS_STYLES[g.status] ?? 'bg-slate-100 text-slate-600')}>
                      {STATUS_LABELS[g.status] ?? g.status}
                    </span>
                  </div>

                  {g.description && (
                    <p className="text-sm text-slate-500 line-clamp-2">{g.description}</p>
                  )}

                  <div>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-slate-500">{filled} / {g.maxParticipants} vagas</span>
                      <span className="font-bold text-teal-700">R$ {Number(g.pricePerSeat).toFixed(2)}</span>
                    </div>
                    <div className="bg-slate-100 rounded-full h-1.5 overflow-hidden">
                      <div className="h-full bg-teal-500" style={{ width: `${pct}%` }} />
                    </div>
                  </div>

                  {g.participants && g.participants.length > 0 && (
                    <div className="flex -space-x-2 pt-1">
                      {g.participants.slice(0, 5).map((p: any) => (
                        p.client?.user?.image ? (
                          <img key={p.id} src={p.client.user.image} alt="" className="size-7 rounded-full ring-2 ring-white object-cover" />
                        ) : (
                          <div key={p.id} className="size-7 rounded-full ring-2 ring-white bg-gradient-to-br from-teal-300 to-coral-300 flex items-center justify-center text-white text-[10px] font-bold">
                            {(p.client?.user?.name ?? '?').charAt(0).toUpperCase()}
                          </div>
                        )
                      ))}
                      {g.participants.length > 5 && (
                        <div className="size-7 rounded-full ring-2 ring-white bg-slate-200 text-slate-600 text-[10px] font-bold flex items-center justify-center">
                          +{g.participants.length - 5}
                        </div>
                      )}
                    </div>
                  )}

                  {isCancellable && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { if (confirm(`Cancelar "${g.title}"?`)) cancelGroup.mutate(g.id); }}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      Cancelar grupo
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function GroupForm({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const { register, handleSubmit, formState: { errors } } = useForm<any>({
    defaultValues: { modality: 'ONLINE', durationMin: 90, maxParticipants: 8 },
  });
  const create = useMutation({
    mutationFn: (data: any) => api.post('groups', { json: data }).json<any>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-groups'] });
      onDone();
    },
  });

  return (
    <Card>
      <CardContent className="p-5">
        <form onSubmit={handleSubmit((d) => create.mutate(d))} className="space-y-4">
          <Field label="Título *" error={errors.title?.message as string | undefined}>
            <input {...register('title', { required: 'Obrigatório' })} className="input" placeholder="Ex: Roda de mulheres" />
          </Field>
          <Field label="Descrição">
            <textarea {...register('description')} rows={3} className="input min-h-20 py-2" />
          </Field>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Data e hora *">
              <input type="datetime-local" {...register('scheduledAt', { required: true })} className="input" />
            </Field>
            <Field label="Duração (min) *">
              <input type="number" min={30} max={240} {...register('durationMin', { valueAsNumber: true })} className="input" />
            </Field>
            <Field label="Máx. participantes *">
              <input type="number" min={2} max={50} {...register('maxParticipants', { valueAsNumber: true })} className="input" />
            </Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Preço por vaga (R$) *">
              <input type="number" min={0} step={1} {...register('pricePerSeat', { required: true, valueAsNumber: true })} className="input" />
            </Field>
            <Field label="Modalidade">
              <select {...register('modality')} className="input">
                <option value="ONLINE">Online</option>
                <option value="PRESENCIAL">Presencial</option>
              </select>
            </Field>
          </div>
          {create.isError && <p className="text-sm text-red-600">Erro ao criar grupo.</p>}
          <Button type="submit" disabled={create.isPending} className="bg-teal-500 hover:bg-teal-600 text-white">
            {create.isPending ? 'Criando...' : 'Criar grupo'}
          </Button>
        </form>
        <style>{`.input{width:100%;background:white;border:1px solid rgb(226 232 240);border-radius:6px;height:38px;padding:0 12px;outline:none;font-size:14px}.input:focus{box-shadow:0 0 0 2px rgb(112 208 192)}`}</style>
      </CardContent>
    </Card>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <div className="mt-1.5">{children}</div>
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </label>
  );
}
